/**
 * BE-shaped data contract for the Sidekick structured-query
 * (NL2SQL) response path. The shapes here mirror the wire format
 * the BE emits via the `visualization` SSE event; renderers under
 * `~/components/MessageDataTable` (and the future `ChartRenderer`
 * umbrella) consume the same surface.
 *
 * `VisualizationData` is a discriminated union keyed by `type`:
 * the `table` arm narrows `dataJson` to {@link TableData}, while
 * the other arms (`bar | line | pie | heatmap`) keep `dataJson`
 * as `unknown` until the upcoming `ChartRenderer` ticket replaces
 * those payloads with per-variant shapes. Consumers should narrow
 * on `viz.type` at the renderer boundary; the discriminator gives
 * them the right `dataJson` typing for free.
 */

/**
 * Visualization variants the BE may emit. The table variant is the
 * only one rendered by this package today; the rest are reserved by
 * the BE for the upcoming `ChartRenderer` ticket.
 */
export const VISUALIZATION_TYPES = [
  'bar',
  'line',
  'pie',
  'table',
  'heatmap',
] as const;

/** One of {@link VISUALIZATION_TYPES}. */
export type VisualizationType = (typeof VISUALIZATION_TYPES)[number];

export type TableColumnType = string;

/**
 * One column descriptor in a `TableData` payload. `name` is the
 * BE-issued snake_case identifier (e.g. `weekly_idling_cost_lcu`);
 * FE humanizes it for display via `humanizeColumnName`.
 */
export type TableColumn = {
  name: string;
  type: TableColumnType;
};

/**
 * One cell value in a `TableData` row. Rows are positional arrays
 * matching the `columns` order; `null` is a valid cell value and
 * renders as the em-dash placeholder in the UI.
 */
export type TableRowValue = string | null;

/**
 * One row in a `TableData` payload, expressed as a positional
 * `ReadonlyArray<TableRowValue>` aligned with the parent payload's
 * `columns` order.
 */
export type TableRow = ReadonlyArray<TableRowValue>;

export type TableData = {
  columns: ReadonlyArray<TableColumn>;
  rows: ReadonlyArray<TableRow>;
  total_row_count: number;
  displayed_row_count: number;
  truncated: boolean;
  title?: string;
};

export type TableVisualization = {
  type: 'table';
  title?: string;
  dataJson: TableData;
  configJson?: unknown;
};

/**
 * BE-shape for the non-table visualization SSE payloads
 * (`bar | line | pie | heatmap`). `dataJson` is intentionally
 * `unknown` until the upcoming `ChartRenderer` ticket lands the
 * per-variant shapes; renderers narrow at their boundary.
 *
 * `configJson` is reserved for variants that need chart-level
 * configuration alongside the data payload.
 */
export type NonTableVisualization = {
  [Type in Exclude<VisualizationType, 'table'>]: {
    type: Type;
    title?: string;
    dataJson: unknown;
    configJson?: unknown;
  };
}[Exclude<VisualizationType, 'table'>];

/**
 * BE-shape for a visualization SSE payload. Discriminated union
 * keyed by `type`: narrowing on `viz.type === 'table'` gives
 * `dataJson: TableData`; narrowing on any other variant gives
 * `dataJson: unknown` until the `ChartRenderer` ticket replaces
 * the non-table arms with per-variant shapes.
 */
export type VisualizationData = TableVisualization | NonTableVisualization;
